import { SvgIcon, Tag } from '@bridebook/aura';
import { Datepicker, MbscDatepickerPageChangeEvent } from '@mobiscroll/react';
import { ComponentProps, ReactNode } from 'react';

export const SUPPLIER_EVENT_BOOKING_STEPS = {
  EVENT_SELECTION: 'event-selection',
  CALENDAR_SELECTION: 'calendar-selection',
  CONTACT_FORM: 'contact-form',
  SUCCESS: 'success',
} as const;

export type SupplierEventBookingStep =
  (typeof SUPPLIER_EVENT_BOOKING_STEPS)[keyof typeof SUPPLIER_EVENT_BOOKING_STEPS];

export type SupplierEventType = 'showround' | 'open-day' | 'custom';

export interface SupplierEventQuestion {
  id?: string;
  text: string;
  required?: boolean;
  type?: 'text';
}

export interface SupplierEventOption {
  id: string;
  eventType: SupplierEventType;
  title: string;
  description?: string;
  dateTimeSummary?: string;
  durationSummary?: string;
  location?: string;
  calendarId?: string;
  questions?: SupplierEventQuestion[];
  icon?: SvgIcon;
  badgeTheme?: ComponentProps<typeof Tag>['theme'];
}

export interface SupplierEventSupplierSummary {
  name?: string;
  subtitle?: string;
  meta?: string;
}

export interface SupplierEventBookingFormValues {
  selectedEventId?: string;
  selectedDate?: string;
  selectedSlot?: string;
  name: string;
  partnerName: string;
  weddingDate: string;
  estimatedGuestCount?: number;
  phoneNumber?: {
    nationalNumber?: string;
    full: string;
    isValid: boolean;
  };
  availabilityPreferences?: string[];
  questionAnswers?: Record<string, string>;
}

export interface SupplierEventBookingSlot {
  id?: string;
  start: string;
  end?: string;
}

export interface SupplierEventBookingCalendarStepProps {
  title: string;
  slots: SupplierEventBookingSlot[];
  selectedDate?: string | null;
  selectedSlot?: string | null;
  timezone?: string;
  locale: string;
  calendarLocale?: ComponentProps<typeof Datepicker>['locale'];
  dateError?: ReactNode;
  slotError?: ReactNode;
  pages?: ComponentProps<typeof Datepicker>['pages'];
  maxAdvance?: string;
  hasAvailability?: boolean;
  reassuranceLabel?: string;
  emptySlotsLabel?: string;
  availabilityMessageMainLabel?: (
    firstAvailableMonthName: string,
    currentMonthNames: string[],
  ) => string;
  availabilityMessageNavigationLabel?: (
    firstAvailableMonthName: string,
    currentMonthNames: string[],
  ) => string;
  onSelectDate: (dateValue: string) => void;
  onSelectSlot: (slotValue: string) => void;
  onPageChange?: (args: MbscDatepickerPageChangeEvent) => void;
  onAvailabilityNavigate?: () => void;
  availableDateHighlights?: ComponentProps<typeof Datepicker>['colors'];
}

export interface SupplierEventBookingCopy {
  eventSelection: {
    title: string;
    subtitle: string;
    infoCardDescription?: string;
  };
}

export interface UseSupplierEventBookingFlowOptions<
  TEvent extends SupplierEventOption = SupplierEventOption,
> {
  initialStep?: SupplierEventBookingStep;
  getEventById: (eventId: string) => TEvent | undefined;
  shouldUseCalendarForEvent: (event: TEvent) => boolean;
  onClose?: () => void;
}

export interface UseSupplierEventBookingFlowResult<
  TEvent extends SupplierEventOption = SupplierEventOption,
> {
  step: SupplierEventBookingStep;
  selectedEventId: string | null;
  selectedDate: string | null;
  selectedSlot: string | null;
  selectedEvent: TEvent | null;
  selectedEventNeedsCalendar: boolean;
  selectEvent: (eventId: string) => void;
  selectDate: (date: string) => void;
  selectSlot: (slotValue: string) => void;
  goToStep: (step: SupplierEventBookingStep) => void;
  goBack: () => void;
  goToDetails: () => void;
  goToSuccess: () => void;
  reset: () => void;
  close: () => void;
}
